import { useContext, useEffect, useRef, useState } from 'react';

import { useRouteService } from '@confluence/content-types-bridge/entry-points/useRouteService';
import type { ContentTypesFlagService } from '@confluence/content-types-bridge/entry-points/content-types-flag-service';
import { useSessionData } from '@confluence/session-data';
import { ExperienceTrackerContext } from '@confluence/experience-tracker';
import {
	INSERT_TEMPLATE_WITH_KEY_QUERY_PARAM,
	useIsWhiteboardFeatureEnabled,
	TEMPLATE_PICKER_QUERY_PARAM,
	JIRA_IMPORT_TOOL_QUERY_PARAM,
	CLOUD_ID,
	PROJECT_UUID,
	PARENT_PRODUCT_QUERY_PARAM,
	EMBEDDED_THEME_STATE,
	EMBEDDED_WHITEBOARD_CREATED_AT_QUERY_PARAM,
} from '@confluence/whiteboard-utils';
import type { ContentFeedbackService } from '@confluence/content-types-bridge/entry-points/content-types-feedback-service';

import { WhiteboardBridgeAPIBuilder } from './whiteboard-bridge-api-builder';
import { getWhiteboardURLBase, getWhiteboardURL } from './whiteboard-url';
import { useWhiteboardQueryParamState } from './useWhiteboardQueryParamState';
import { useContainerService } from './useContainerService';
import { useNavigationService } from './useNavigationService';
import { useOrgDataService } from './useOrgDataService';
import { useTokenService } from './useTokenService';
import type { WhiteboardBridgeAPI } from './whiteboard-bridge-api-type';
import type { UseSpaceQueryResult } from './graphql/useSpace';
import type { GetWhiteboardParamsResult } from './whiteboard-params';
import type { WhiteboardEditionUpgradeService } from './services/whiteboard-edition-upgrade-service';
import type { WhiteboardOnboardingService } from './services/whiteboard-onboarding-service';
import { useThemingService } from './useThemingService';
import type { WhiteboardResolvedCommentsService } from './services/whiteboard-resolved-comment-service';
import type { WhiteboardInternalModalService } from './services/whiteboard-internal-modal-service';
import type { integrationService } from './useIntegrationService';
import type { WhiteboardEventService } from './services/whiteboard-event-service';

export interface UseWhiteboardBridgeAPIOptions {
	version: string | null;
	relaySpot?: number;
	serverSpot?: number;
	whiteboardLocale?: string;
	whiteboardLinkedObject?: string;
	focusedCommentId?: string;
	replyToComment?: string;
	space: UseSpaceQueryResult;
	spaceKey: string;
	contentId: string;
	referenceId?: string | null;
	shareToken?: string;
	params?: GetWhiteboardParamsResult;
	refetch?: () => void;
	services: {
		editionUpgradeService: WhiteboardEditionUpgradeService;
		feedbackService: ContentFeedbackService;
		onboardingService: WhiteboardOnboardingService;
		resolvedCommentService: WhiteboardResolvedCommentsService;
		whiteboardInternalModalService: WhiteboardInternalModalService;
		eventService: WhiteboardEventService;
		// For triggering flags in CFE
		flagService: ContentTypesFlagService;
		integrationService: integrationService;
	};
	onThemeLoaded: () => void;
}

export const useWhiteboardBridgeAPI = ({
	version,
	relaySpot,
	serverSpot,
	whiteboardLocale,
	whiteboardLinkedObject,
	focusedCommentId,
	replyToComment,
	space,
	spaceKey,
	contentId,
	referenceId,
	shareToken,
	params,
	refetch,
	services: {
		editionUpgradeService,
		feedbackService,
		onboardingService,
		resolvedCommentService,
		eventService,
		whiteboardInternalModalService,
		flagService,
		integrationService,
	},
	onThemeLoaded,
}: UseWhiteboardBridgeAPIOptions) => {
	const { userId: accountId, cloudId: tenantId } = useSessionData();
	const { isWhiteboardFeatureEnabled, platformFeatureFlagResolver } =
		useIsWhiteboardFeatureEnabled();

	const experienceTracker = useContext(ExperienceTrackerContext);

	const openTemplatePickerState = useWhiteboardQueryParamState(
		contentId,
		TEMPLATE_PICKER_QUERY_PARAM,
	);
	const openTemplatePicker =
		openTemplatePickerState !== undefined ? openTemplatePickerState === 'true' : undefined;

	const jiraImportToolState = useWhiteboardQueryParamState(contentId, JIRA_IMPORT_TOOL_QUERY_PARAM);
	const openJiraImportTool =
		jiraImportToolState !== undefined ? jiraImportToolState === 'true' : undefined;

	const insertTemplateWithKey = useWhiteboardQueryParamState(
		contentId,
		INSERT_TEMPLATE_WITH_KEY_QUERY_PARAM,
	);
	const parentProduct = useWhiteboardQueryParamState(contentId, PARENT_PRODUCT_QUERY_PARAM, false);
	const embeddedThemeState = useWhiteboardQueryParamState(contentId, EMBEDDED_THEME_STATE, false);

	const createdAt = useWhiteboardQueryParamState(
		contentId,
		EMBEDDED_WHITEBOARD_CREATED_AT_QUERY_PARAM,
		false,
	);

	const cloudId = useWhiteboardQueryParamState(contentId, CLOUD_ID);
	const projectUuid = useWhiteboardQueryParamState(contentId, PROJECT_UUID);
	const { themingService, initialThemeSerialized } = useThemingService();
	const isSSR = Boolean(process.env.REACT_SSR);
	// Keep the `src` attribute of the iframe the same, and instead use the iframe's
	// location API to update the URL. We *need* to do this, as directly updating the `src`
	// attribute will push to the history stack in addition to react-router, breaking the
	// browser back/forward button.
	const url = getWhiteboardURL(referenceId, {
		// Don't show the template picker while the referenceId is loading
		openTemplatePicker: referenceId ? openTemplatePicker : undefined,
		openJiraImportTool: referenceId ? openJiraImportTool : undefined,
		cloudId: openJiraImportTool ? cloudId : undefined,
		projectUuid: openJiraImportTool ? projectUuid : undefined,
		insertTemplateWithKey: referenceId ? insertTemplateWithKey : undefined,
		headless: params?.headless,
		canvasServerSpot: serverSpot,
		canvasRelaySpot: relaySpot,
		whiteboardLocale,
		whiteboardLinkedObject,
		initialTheme: embeddedThemeState || initialThemeSerialized,
		parentProduct,
		createdAt,
		focusedCommentId,
		replyToComment,
	});

	const initialUrl = useRef(url);

	const navigationService = useNavigationService(spaceKey);
	const tokenService = useTokenService({
		tenantId,
		space,
		referenceId,
		shareToken,
		contentId,
		refetch,
	});
	const routeService = useRouteService(url);
	const containerService = useContainerService(space, spaceKey);
	const orgDataService = useOrgDataService();

	const [bridgeApi] = useState<WhiteboardBridgeAPI>(() => {
		const context = {
			sessionData: {
				accountId:
					// For anonymous users, pass in unidentified value so canvas can process
					!accountId ? 'unidentified' : accountId,
				tenantId,
			},
		};

		const services = {
			containerService,
			experienceTracker,
			isFeatureActive: isWhiteboardFeatureEnabled,
			platformFeatureFlagResolver,
			navigationService,
			onboardingService,
			orgDataService,
			routeService,
			tokenService,
			editionUpgradeService,
			themingService,
			feedbackService,
			onThemeLoaded,
			resolvedCommentService,
			eventService,
			whiteboardInternalModalService,
			flagService,
			integrationService,
		};

		return WhiteboardBridgeAPIBuilder.with(context, services).build();
	});

	// Don't add bridge API to window object in SSR
	if (!isSSR) {
		window.contentAPI = bridgeApi;
	}

	// Make sure we clean up the whiteboard bridge API when unmounting
	useEffect(() => {
		return () => {
			delete window.contentAPI;
		};
	}, []);

	return {
		initialUrl: `${getWhiteboardURLBase(version, parentProduct)}${initialUrl.current}`,
	};
};
